import {
  TTableStateBlob,
  TTableViewSnapshot,
} from '@/shared/features/filter-preferences/storage/table-state-storage/table-state-storage.types';
import { TStoredColumnState } from '@/shared/types/ui-preferences.types';

const STORAGE_KEY = 'sda:table-state';
const VERSION = 1;

const EMPTY_BLOB: TTableStateBlob = { v: VERSION, tenants: {} };

const readBlob = (): TTableStateBlob => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_BLOB;

    const blob = JSON.parse(raw) as TTableStateBlob;
    if (blob?.v !== VERSION || !blob.tenants) return EMPTY_BLOB;

    return blob;
  } catch {
    // Remembering the last view is a convenience, never a correctness requirement.
    return EMPTY_BLOB;
  }
};

const writeBlob = (blob: TTableStateBlob): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch {
    // Quota exceeded or storage blocked -- non-fatal, just skip remembering.
  }
};

export const readTableView = (
  tenantId: string,
  scopeKey: string,
): TTableViewSnapshot | undefined => readBlob().tenants[tenantId]?.[scopeKey];

export const writeTableView = (
  tenantId: string,
  scopeKey: string,
  patch: TTableViewSnapshot,
): void => {
  const blob = readBlob();
  const scopes = blob.tenants[tenantId] ?? {};

  writeBlob({
    ...blob,
    tenants: {
      ...blob.tenants,
      [tenantId]: {
        ...scopes,
        [scopeKey]: { ...scopes[scopeKey], ...patch },
      },
    },
  });
};

// The saved view only seeds a scope with nothing remembered yet: from then on
// the local layout is the one that gets applied, so it must outrank it.
export const seedTableViewColumns = (
  tenantId: string,
  scopeKey: string,
  columns: TStoredColumnState[],
): void => {
  if (!columns.length || readTableView(tenantId, scopeKey)?.columns?.length) {
    return;
  }

  writeTableView(tenantId, scopeKey, { columns });
};

export const clearTableView = (tenantId: string, scopeKey: string): void => {
  const blob = readBlob();
  const scopes = blob.tenants[tenantId];
  if (!scopes?.[scopeKey]) return;

  const { [scopeKey]: _removed, ...rest } = scopes;

  writeBlob({ ...blob, tenants: { ...blob.tenants, [tenantId]: rest } });
};

export const hasAnyTableView = (tenantId: string): boolean =>
  Object.keys(readBlob().tenants[tenantId] ?? {}).length > 0;

export const clearAllTableViews = (tenantId: string): void => {
  const blob = readBlob();
  if (!blob.tenants[tenantId]) return;

  const { [tenantId]: _removed, ...tenants } = blob.tenants;

  writeBlob({ ...blob, tenants });
};
